// ── Design decisions ──
// Botón chico "Catálogo" al lado del artículo. Al pasar el mouse o enfocarlo
// (con 200 ms de espera y mínimo 3 letras en la descripción) consulta el
// catálogo DGA y muestra una tarjeta flotante con, como máximo, 3 filas:
// descripción + "CIP $x.xx". NUNCA elige sola — solo un clic (o Enter) en una
// fila llama a onLock(row); así el FOB que entra al anexo siempre lo decidió
// el operador. En tablet no hay hover: el tap en el botón abre/cierra la
// tarjeta. Si el artículo ya está ligado (customs_catalog_id) el botón se
// reemplaza por el chip verde "Catálogo ✓" + "Desligar" (onLock(null)).
// La última respuesta gana: un contador de solicitud descarta las viejas.

import { useEffect, useRef, useState } from 'react';
import { Check, Search } from 'lucide-react';
import { fmtUsd } from './labels';

const MAX_FILAS = 3;
const MIN_CHARS = 3;

const CatalogHoverPicker = ({ item, searchCatalog, onLock, disabled = false, debounceMs = 200 }) => {
  const [abierto, setAbierto] = useState(false);
  const [estado, setEstado] = useState('idle'); // idle | loading | ok | error
  const [filas, setFilas] = useState([]);
  const [resaltado, setResaltado] = useState(0);
  const timer = useRef(null);
  const solicitud = useRef(0);
  const cerrarTimer = useRef(null);

  const q = String(item?.descripcion || '').trim();
  const ligado = Boolean(item?.customs_catalog_id);
  const puedeBuscar = q.length >= MIN_CHARS && typeof searchCatalog === 'function';

  useEffect(
    () => () => {
      clearTimeout(timer.current);
      clearTimeout(cerrarTimer.current);
    },
    []
  );

  // Cada apertura dispara una búsqueda (debounced). Cerrar cancela la pendiente.
  useEffect(() => {
    if (!abierto) {
      clearTimeout(timer.current);
      return undefined;
    }
    if (!puedeBuscar) {
      setEstado('ok');
      setFilas([]);
      return undefined;
    }
    setEstado('loading');
    const id = ++solicitud.current;
    timer.current = setTimeout(async () => {
      try {
        const res = await searchCatalog(q);
        if (id !== solicitud.current) return;
        setFilas(Array.isArray(res) ? res.slice(0, MAX_FILAS) : []);
        setResaltado(0);
        setEstado('ok');
      } catch (err) {
        if (id !== solicitud.current) return;
        setFilas([]);
        setEstado('error');
      }
    }, debounceMs);
    return () => clearTimeout(timer.current);
  }, [abierto, q, puedeBuscar, searchCatalog, debounceMs]);

  // El hover/focus abre "de pasada"; el clic (tap) fija. Si la tarjeta se
  // abrió por hover, el primer clic la fija en vez de cerrarla — así un tap en
  // tablet (que dispara mouseenter + click) la deja abierta y el segundo la cierra.
  const abiertaDePasada = useRef(false);
  const abrir = () => {
    clearTimeout(cerrarTimer.current);
    if (disabled) return;
    if (!abierto) abiertaDePasada.current = true;
    setAbierto(true);
  };
  const alternar = () => {
    if (disabled) return;
    if (abierto && !abiertaDePasada.current) {
      cerrar();
      return;
    }
    clearTimeout(cerrarTimer.current);
    abiertaDePasada.current = false;
    setAbierto(true);
  };
  const cerrarConDemora = () => {
    clearTimeout(cerrarTimer.current);
    cerrarTimer.current = setTimeout(() => setAbierto(false), 150);
  };
  const cerrar = () => {
    clearTimeout(cerrarTimer.current);
    setAbierto(false);
  };

  const elegir = (row) => {
    cerrar();
    onLock && onLock(row);
  };

  const onKeyDownTrigger = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      cerrar();
      return;
    }
    if (!abierto) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        abrir();
      }
      return;
    }
    if (filas.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setResaltado((i) => (i + 1) % filas.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setResaltado((i) => (i - 1 + filas.length) % filas.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      elegir(filas[resaltado]);
    }
  };

  if (ligado) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span
          className="inline-flex items-center gap-1 rounded-full border border-green-300 bg-green-50 px-2 py-0.5 text-[10px] font-semibold text-green-700"
          title="Ligado al catálogo DGA"
        >
          <Check size={10} aria-hidden="true" /> Catálogo ✓
        </span>
        {!disabled && (
          <button
            type="button"
            onClick={() => onLock && onLock(null)}
            aria-label="Desligar del catálogo"
            title="Desligar del catálogo: vuelve a valores manuales"
            className="rounded-md px-1.5 py-0.5 text-[11px] text-slate-500 underline-offset-2 hover:text-slate-900 hover:underline"
          >
            Desligar
          </button>
        )}
      </span>
    );
  }

  return (
    <span
      className="relative inline-flex"
      onMouseEnter={abrir}
      onMouseLeave={cerrarConDemora}
    >
      <button
        type="button"
        aria-label="Buscar en el catálogo DGA"
        aria-expanded={abierto}
        aria-haspopup="listbox"
        title="Buscar en el catálogo DGA (Enter abre, ↑ ↓ para moverse, Enter elige, Esc cierra)"
        disabled={disabled}
        onClick={alternar}
        onFocus={abrir}
        onBlur={cerrarConDemora}
        onKeyDown={onKeyDownTrigger}
        className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] transition-colors disabled:opacity-50 ${
          abierto
            ? 'border-[var(--brand-teal)] bg-[var(--brand-teal-soft)] text-slate-900'
            : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
        }`}
      >
        <Search size={12} aria-hidden="true" /> Catálogo
      </button>

      {abierto && (
        <div
          role="listbox"
          aria-label="Coincidencias del catálogo DGA"
          onMouseEnter={abrir}
          onMouseLeave={cerrarConDemora}
          className="absolute left-0 top-full z-30 mt-1 w-72 overflow-hidden rounded-md border border-slate-200 bg-white text-xs shadow-lg"
        >
          {!puedeBuscar ? (
            <p className="px-2.5 py-2 text-slate-500">
              Escribí al menos {MIN_CHARS} letras en la descripción para buscar.
            </p>
          ) : estado === 'loading' ? (
            <p className="px-2.5 py-2 text-slate-500" aria-live="polite">
              Buscando…
            </p>
          ) : estado === 'error' ? (
            <p className="px-2.5 py-2 text-red-600" role="alert">
              No se pudo consultar el catálogo.
            </p>
          ) : filas.length === 0 ? (
            <p className="px-2.5 py-2 text-slate-500">Sin coincidencias — queda manual.</p>
          ) : (
            filas.map((row, i) => (
              <div
                key={row.id ?? i}
                role="option"
                tabIndex={-1}
                aria-selected={i === resaltado}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setResaltado(i)}
                onClick={() => elegir(row)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    elegir(row);
                  }
                }}
                className={`flex cursor-pointer items-center justify-between gap-2 px-2.5 py-1.5 ${
                  i === resaltado ? 'bg-[var(--brand-teal-soft)] text-slate-900' : 'text-slate-700'
                }`}
              >
                <span className="truncate" title={row.descripcion}>
                  {row.descripcion}
                </span>
                <span className="shrink-0 font-mono text-[11px] font-semibold text-slate-900">
                  CIP ${fmtUsd(row.cip_usd)}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </span>
  );
};

export default CatalogHoverPicker;
